import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, Link, useLoaderData, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { useSessionDetail } from "@/services/dexie"

import { BackButton } from "../../../../components/buttons/BackButton"
import { ExtraIcons } from "../../../../components/icons/InstructionImageType"
import { BaseLayout } from "../../../../components/layouts/BaseLayout"
import { ImageLayout } from "../../../../components/layouts/ImageLayout"
import { useGeolocationRef } from "../../../../helpers/GeolocationProvider"
import { useImagePickerWithRotation } from "../../../../helpers/hooks/useImagePicker"
import { useUpsertPhotoHook } from "../../../../helpers/hooks/useStore"
import { IconCam } from "../../../../images/xd/icons/IconCam"
import { LINKS } from "../../../../routing/links"
import { DamageMarkers, Marker } from "../../../../sections/damage/DamageMarkers"
import { useGuardedAction } from "../../../../utils/hooks/useGuardedAction"
import { useBacklink } from "../../../../utils/session"

export const Route = createFileRoute("/$sessionId/_session/damage/new")({
  component: DamageNew,
})

type DamageNewPhotoProps = {
  onPhotoCapture: () => void
  enableUpload: boolean
}

function DamageNewPhoto(props: DamageNewPhotoProps) {
  const { onPhotoCapture, enableUpload } = props

  const { sessionId } = Route.useParams()
  const { t } = useTranslation()
  const backlink = useBacklink()

  return (
    <>
      {backlink && <BackButton backlink={backlink} />}

      <ImageLayout
        iconName={ExtraIcons.DamageDetail}
        content={
          <>
            <Heading as="h2" variant="h1">
              {t("damage.capture.title")}
            </Heading>

            <div className="h-6 w-6 shrink-0" />

            <Text>{t("damage.capture.text")}</Text>
          </>
        }
        footer={
          <>
            <div className="h-6 w-6 shrink-0" />

            <div className="flex flex-col justify-center">
              <Button
                data-testid="damage-capture-upload-btn"
                type="button"
                onClick={onPhotoCapture}
                icon={<IconCam className="text-ic-background w-8" />}
              >
                <span>{enableUpload ? t("guide.uploadPhoto") : t("guide.takePhoto")}</span>
              </Button>

              <div className="h-2 w-2 shrink-0" />

              <Link to={LINKS.damage(sessionId)}>
                <Button onClick={() => {}} variant="secondary" as="span">
                  {t("damage.capture.secondaryActionTitle")}
                </Button>
              </Link>
            </div>

            <div className="h-11 w-11 shrink-0" />
          </>
        }
      />
    </>
  )
}

function DamageNew() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const backlink = useBacklink()

  const { sessionId } = Route.useParams()
  const { enableUpload } = useLoaderData({ from: "/$sessionId/_session/damage" })
  const { data: session } = useSessionDetail(sessionId)

  const upsertPhoto = useUpsertPhotoHook()
  const { getCurrentGeolocation } = useGeolocationRef()

  const {
    files,
    file,
    select: openImagePicker,
    reset: clearImageFromPicker,
  } = useImagePickerWithRotation({ enableUpload })

  const [markers, setMarkers] = useState<Marker[]>(session?.markers ?? [])

  const [saveDamage, isSaving] = useGuardedAction(async () => {
    const coords = getCurrentGeolocation()
    const location = coords
      ? { latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy ?? -1 }
      : null

    await upsertPhoto({
      sessionId,
      type: "Damage",
      files,
      markers,
      location,
      retries: 0,
      canUploadMultiple: true,
    })

    navigate({ to: backlink ? backlink : LINKS.damageAddMore(sessionId) })
  })

  if (!file) {
    return <DamageNewPhoto onPhotoCapture={openImagePicker} enableUpload={enableUpload} />
  }

  return (
    <BaseLayout>
      {/* Na nízkém landscape viewportu (viz IC-2970) se sloupcové rozložení (siluetka nad textem)
          nevejde do výšky, proto se přepíná na řádkové: siluetka vlevo, text a tlačítka vpravo. */}
      <div className="h-vh-header landscape-short:flex-row landscape-short:justify-start flex flex-col items-center justify-between">
        <DamageMarkers markers={markers?.length ? markers : []} onChange={setMarkers} />

        <div className="landscape-short:h-px h-6 w-6 shrink-0" />

        <div className="landscape-short:w-auto landscape-short:shrink-0 w-full max-w-120 items-center px-8">
          <Heading as="h2" variant="h1" className="landscape-short:text-sm/[18px]">
            {t("damage.select.title")}
          </Heading>

          <div className="landscape-short:h-1 h-6 w-6 shrink-0" />

          <Text className="landscape-short:text-xs">
            <Trans
              i18nKey="damage.select.text"
              components={{
                br: <br />,
                bold: <strong className="font-bold" />,
              }}
            />
          </Text>

          <div className="landscape-short:h-2 h-12 w-12 shrink-0" />

          <div className="flex flex-col justify-center">
            <Button
              data-testid="damage-save-btn"
              type="button"
              loading={isSaving}
              disabled={markers.length === 0 || isSaving}
              onClick={saveDamage}
            >
              <span>{t("damage.select.primaryActionTitle")}</span>
            </Button>

            <div className="h-2 w-2 shrink-0" />

            <Button type="button" onClick={clearImageFromPicker} variant="secondary" disabled={isSaving}>
              {t("damage.select.secondaryActionTitle")}
            </Button>
          </div>

          <div className="landscape-short:h-2 h-11 w-11 shrink-0" />
        </div>
      </div>
    </BaseLayout>
  )
}
